import { supabase, supabaseUrl } from '../lib/supabase'
import {
  MAX_ATTACHMENT_SIZE,
  formatFileSize as formatBytes,
  guessMimeType,
  validateAttachment as validateAttachmentFile,
} from '../utils/attachmentLimits'
import { uploadAttachmentFile } from '../utils/attachmentUpload'
import { jstDateString } from '../utils/jstDate'

export interface OverallSummary {
  summary: string
  strengths: string
  challenges: string
  support_policy: string
  generated_at: string
}

export interface Homework {
  id: string
  type: 'system' | 'entre' | 'school'
  category: string | null
  title: string
  description: string | null
  related_video_id: string | null
  scoring_prompt: string
  initial_message: string | null
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
  school_id: string | null
  deadline_at: string | null
  start_at: string | null
  target_grades: string[]
  target_classes: string[]
  target_school_ids: string[]
  overall_summary: OverallSummary | null
}

/**
 * Homework types that offer file attachments. System homework (みらいの宿題) is answered
 * in the text box alone; school and entre submissions can carry the work as files.
 */
export function supportsAttachments(type: Homework['type']): boolean {
  return type === 'school' || type === 'entre'
}

export interface SchoolStudent {
  id: string
  name: string
  email?: string | null
  grade: string | null
  class: string | null
  attendance_number: string | null
}

export interface HomeworkSubmission {
  id: string
  student_id: string
  homework_id: string
  submitted_answer: string
  conversation_id: string | null
  grade: 'A' | 'B' | 'C' | null
  feedback_text: string | null
  status: 'draft' | 'submitted' | 'graded'
  submitted_at: string
  graded_at: string | null
  teacher_reviewed: boolean
  reviewed_by: string | null
  homework?: Homework
}

export interface SchoolHomeworkSubmissionWithStudent extends HomeworkSubmission {
  student: {
    id: string
    name: string
    grade: string | null
    class: string | null
    school_id: string | null
  } | null
}

/** アントレ部の名簿は複数校にまたがるので、生徒がどの学校かを併せて持つ */
export interface EntreClubStudent extends SchoolStudent {
  school_id: string | null
  school_name: string | null
}

export interface HomeworkSelection {
  student_id: string
  homework_id: string
  selected_at: string
  homework?: Homework
}

export interface HomeworkChatMessageRow {
  id: string
  student_id: string
  homework_id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
}

export interface HomeworkAttachment {
  id: string
  student_id: string
  homework_id: string
  file_name: string
  file_path: string
  file_size: number
  mime_type: string
  uploaded_by: string | null
  created_at: string
}

const SCHOOL_SUBMISSION_SELECT =
  '*, homework:homeworks(*), student:users!student_id(id, name, grade, class, school_id)'

// 学校で絞るときは埋め込みを !inner にしないと student.school_id で filter できない
// （getSubmissionStats と同じやり方）
const SCHOOL_SUBMISSION_SELECT_SCOPED =
  '*, homework:homeworks(*), student:users!student_id!inner(id, name, grade, class, school_id)'

// 開始日〜締切日（JSTの暦日。締切当日は含む）の間にある宿題か。締切・開始日なしは制限なし
function isWithinPeriod(hw: Homework): boolean {
  const today = jstDateString(new Date())
  if (hw.deadline_at && jstDateString(hw.deadline_at) < today) return false
  if (hw.start_at && jstDateString(hw.start_at) > today) return false
  return true
}

export interface SubmissionStats {
  submitted: number
  unreviewed: number
  submittedStudentIds: Set<string>
}

export class HomeworkService {
  // --- Student methods ---

  static async getSystemHomework(): Promise<Homework[]> {
    const { data, error } = await supabase
      .from('homeworks')
      .select('*')
      .eq('type', 'system')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
    if (error) throw error
    return data as Homework[]
  }

  // IDs of the homework this student has submitted. Drafts (途中保存) don't count as
  // submitted — the homework should stay in the 現在の宿題 section.
  static async getSubmittedHomeworkIds(studentId: string): Promise<Set<string>> {
    const { data, error } = await supabase
      .from('homework_submissions')
      .select('homework_id')
      .eq('student_id', studentId)
      .neq('status', 'draft')
    if (error) throw error
    return new Set((data ?? []).map((s: { homework_id: string }) => s.homework_id))
  }

  // 現在の宿題に出す学校宿題すべて（未提出・期間内・対象学年／クラス）。締切が近い順、締切なしは最後。
  // submittedHomeworkIds: pass it when the caller is already loading the student's
  // submissions, so the homework query and that load run side by side instead of
  // this function fetching the submissions again after its own query.
  static async getActiveSchoolHomeworks(params: {
    studentId: string
    schoolId: string
    grade: string | null
    className: string | null
    submittedHomeworkIds?: Promise<Set<string>>
  }): Promise<Homework[]> {

    const submittedIdsPromise = params.submittedHomeworkIds ?? this.getSubmittedHomeworkIds(params.studentId)
    submittedIdsPromise.catch(() => {}) // a homework-query error below should not leave this rejection unhandled
    const { data, error } = await supabase
      .from('homeworks')
      .select('*')
      .eq('type', 'school')
      .eq('is_active', true)
      .eq('school_id', params.schoolId)
      .order('deadline_at', { ascending: true, nullsFirst: false })
    if (error) throw error

    const candidates = (data as Homework[]).filter(hw => {
      if (!isWithinPeriod(hw)) return false

      // Filter by target_grades: empty array or ['all'] means all students
      const grades = hw.target_grades ?? []
      if (grades.length > 0 && !grades.includes('all')) {
        if (!params.grade || !grades.includes(params.grade)) return false
      }

      // Filter by target_classes: same logic
      const classes = hw.target_classes ?? []
      if (classes.length > 0 && !classes.includes('all')) {
        if (!params.className || !classes.includes(params.className)) return false
      }

      return true
    })

    // Exclude already-submitted homework.
    const submittedIds = await submittedIdsPromise
    return candidates.filter(hw => !submittedIds.has(hw.id))
  }

  static async getActiveEntreHomeworks(params: {
    studentId: string
    schoolId: string
    submittedHomeworkIds?: Promise<Set<string>>
  }): Promise<Homework[]> {

    const submittedIdsPromise = params.submittedHomeworkIds ?? this.getSubmittedHomeworkIds(params.studentId)
    submittedIdsPromise.catch(() => {}) // a homework-query error below should not leave this rejection unhandled
    const { data, error } = await supabase
      .from('homeworks')
      .select('*')
      .eq('type', 'entre')
      .eq('is_active', true)
      .order('deadline_at', { ascending: true, nullsFirst: false })
    if (error) throw error

    const candidates = (data as Homework[]).filter(hw => {
      if (!isWithinPeriod(hw)) return false
      if (hw.target_school_ids.length > 0 && !hw.target_school_ids.includes(params.schoolId)) return false
      return true
    })

    const submittedIds = await submittedIdsPromise
    return candidates.filter(hw => !submittedIds.has(hw.id))
  }

  static async getHomeworkById(id: string): Promise<Homework> {
    const { data, error } = await supabase
      .from('homeworks')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data as Homework
  }

  static async getMySubmissions(studentId: string): Promise<HomeworkSubmission[]> {
    // Drafts (途中保存) are excluded — they belong in 現在の宿題, not the
    // 提出済みの宿題 list.
    const { data, error } = await supabase
      .from('homework_submissions')
      .select('*, homework:homeworks(*)')
      .eq('student_id', studentId)
      .neq('status', 'draft')
      .order('submitted_at', { ascending: false })
    if (error) throw error
    return data as HomeworkSubmission[]
  }

  // --- Homework chat persistence (per student + homework) ---

  static async getChatMessages(studentId: string, homeworkId: string): Promise<HomeworkChatMessageRow[]> {
    const { data, error } = await supabase
      .from('homework_chat_messages')
      .select('*')
      .eq('student_id', studentId)
      .eq('homework_id', homeworkId)
      .order('created_at', { ascending: true })
    if (error) throw error
    return data as HomeworkChatMessageRow[]
  }

  static async addChatMessage(
    studentId: string,
    homeworkId: string,
    role: 'user' | 'assistant',
    content: string
  ): Promise<void> {
    const { error } = await supabase
      .from('homework_chat_messages')
      .insert({ student_id: studentId, homework_id: homeworkId, role, content })
    if (error) throw error
  }

  // Delete the entire persisted chat for this student + homework (チャットをリセット).
  static async deleteChatMessages(studentId: string, homeworkId: string): Promise<void> {
    const { error } = await supabase
      .from('homework_chat_messages')
      .delete()
      .eq('student_id', studentId)
      .eq('homework_id', homeworkId)
    if (error) throw error
  }

  static async getSubmission(studentId: string, homeworkId: string): Promise<HomeworkSubmission | null> {
    const { data, error } = await supabase
      .from('homework_submissions')
      .select('*, homework:homeworks(*)')
      .eq('student_id', studentId)
      .eq('homework_id', homeworkId)
      .maybeSingle()
    if (error) throw error
    return data as HomeworkSubmission | null
  }

  // Save an in-progress answer without submitting it for grading (途中保存).
  // Upserts a homework_submissions row with status 'draft'; any previous grade
  // is cleared so a re-edited school submission drops out of the grading queue
  // until it's submitted again.
  static async saveDraft(params: {
    studentId: string
    homeworkId: string
    answer: string
  }): Promise<void> {
    const { error } = await supabase
      .from('homework_submissions')
      .upsert(
        {
          student_id: params.studentId,
          homework_id: params.homeworkId,
          submitted_answer: params.answer,
          status: 'draft',
          grade: null,
          feedback_text: null,
          graded_at: null,
          teacher_reviewed: false,
          reviewed_by: null,
        },
        { onConflict: 'student_id,homework_id' }
      )
    if (error) throw error
  }

  // For system/entre homework: AI grade is final, immediately visible to student
  static async updateGrade(
    submissionId: string,
    grade: 'A' | 'B' | 'C',
    feedbackText: string
  ): Promise<void> {
    const { error } = await supabase
      .from('homework_submissions')
      .update({
        grade,
        feedback_text: feedbackText,
        status: 'graded',
        graded_at: new Date().toISOString(),
        teacher_reviewed: true
      })
      .eq('id', submissionId)
    if (error) throw error
  }

  // For school homework: AI grade saved but teacher_reviewed stays false until teacher confirms
  static async updateAIGrade(
    submissionId: string,
    grade: 'A' | 'B' | 'C',
    feedbackText: string
  ): Promise<void> {
    const { error } = await supabase
      .from('homework_submissions')
      .update({
        grade,
        feedback_text: feedbackText,
        status: 'graded',
        graded_at: new Date().toISOString(),
        teacher_reviewed: false
      })
      .eq('id', submissionId)
    if (error) throw error
  }

  static async upsertSystemHomeworkSelection(studentId: string, homeworkId: string): Promise<void> {
    const { error } = await supabase
      .from('homework_selections')
      .upsert(
        { student_id: studentId, homework_id: homeworkId, selected_at: new Date().toISOString() },
        { onConflict: 'student_id' }
      )
    if (error) throw error
  }

  static async getSystemHomeworkSelection(studentId: string): Promise<HomeworkSelection | null> {
    const { data, error } = await supabase
      .from('homework_selections')
      .select('*, homework:homeworks(*)')
      .eq('student_id', studentId)
      .maybeSingle()
    if (error) throw error
    return data as HomeworkSelection | null
  }

  // --- Submission attachments (school homework: attach files to a submission) ---

  private static readonly ATTACHMENT_BUCKET = 'homework-attachments'

  // Limits live in utils/attachmentLimits so both upload paths stay in step with the
  // buckets' file_size_limit / allowed_mime_types.
  static readonly MAX_ATTACHMENT_SIZE = MAX_ATTACHMENT_SIZE

  // Client-side validation before upload. Returns an error message, or null when OK.
  static validateAttachment(file: File): string | null {
    return validateAttachmentFile(file)
  }

  static async getSubmissionAttachments(studentId: string, homeworkId: string): Promise<HomeworkAttachment[]> {
    const { data, error } = await supabase
      .from('homework_submission_attachments')
      .select('*')
      .eq('student_id', studentId)
      .eq('homework_id', homeworkId)
      .order('created_at', { ascending: true })
    if (error) throw error
    return data as HomeworkAttachment[]
  }

  static async uploadSubmissionAttachment(params: {
    studentId: string
    homeworkId: string
    file: File
    uploadedBy: string
  }): Promise<HomeworkAttachment> {
    const { studentId, homeworkId, file, uploadedBy } = params
    // Path: {student_id}/{homework_id}/... so storage RLS can key off foldername[1].
    const fileExt = file.name.split('.').pop()
    const filePath = `${studentId}/${homeworkId}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`

    // Sends the bytes straight to Supabase when the file is too big for the /api proxy.
    const uploadData = await uploadAttachmentFile({
      bucket: this.ATTACHMENT_BUCKET,
      path: filePath,
      file,
    })

    const { data, error } = await supabase
      .from('homework_submission_attachments')
      .insert({
        student_id: studentId,
        homework_id: homeworkId,
        file_name: file.name,
        file_path: uploadData.path,
        file_size: file.size,
        mime_type: guessMimeType(file) || file.type,
        uploaded_by: uploadedBy,
      })
      .select()
      .single()
    if (error) {
      // Roll back the uploaded file if the metadata insert fails.
      await supabase.storage.from(this.ATTACHMENT_BUCKET).remove([uploadData.path])
      throw error
    }
    return data as HomeworkAttachment
  }

  static async deleteSubmissionAttachment(attachment: HomeworkAttachment): Promise<void> {
    const { error } = await supabase
      .from('homework_submission_attachments')
      .delete()
      .eq('id', attachment.id)
    if (error) throw error
    // Best-effort storage cleanup; the metadata row is already gone.
    const { error: storageError } = await supabase.storage
      .from(this.ATTACHMENT_BUCKET)
      .remove([attachment.file_path])
    if (storageError) console.warn('Failed to remove attachment from storage:', storageError)
  }

  // Signed URL (private bucket). Short-lived; download forces the original filename.
  static async getAttachmentDownloadUrl(filePath: string, fileName: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(this.ATTACHMENT_BUCKET)
      .createSignedUrl(filePath, 60, { download: fileName })
    if (error || !data) throw error ?? new Error('ダウンロードURLの生成に失敗しました')
    return data.signedUrl
  }

  static formatFileSize(bytes: number): string {
    return formatBytes(bytes)
  }

  static getFileIcon(mimeType: string): string {
    if (mimeType.includes('pdf')) return '📄'
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝'
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊'
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📋'
    if (mimeType.includes('image')) return '🖼️'
    return '📎'
  }

  // --- Teacher methods ---

  static async getSchoolHomeworkList(schoolId: string): Promise<Homework[]> {
    const { data, error } = await supabase
      .from('homeworks')
      .select('*')
      .eq('type', 'school')
      .eq('school_id', schoolId)
      .order('created_at', { ascending: false })
    if (error) throw error
    return data as Homework[]
  }

  /**
   * アントレ部宿題は全校共通（school_id は NULL）。target_school_ids が空配列なら全学校、
   * そうでなければ含まれる学校だけが対象。行数が少ないので絞り込みは getActiveEntreHomeworks
   * と同じくクライアント側で行う（配列リテラルを .or() に書くより読みやすい）。
   */
  static async getEntreHomeworkListForSchool(schoolId: string): Promise<Homework[]> {
    return (await this.getEntreHomeworkList()).filter(hw => {
      const targets = hw.target_school_ids ?? []
      return targets.length === 0 || targets.includes(schoolId)
    })
  }

  // 対象校で絞らない全アントレ部宿題（管理者用の学校を選んでいる管理者だけが使う）
  static async getEntreHomeworkList(): Promise<Homework[]> {
    const { data, error } = await supabase
      .from('homeworks')
      .select('*')
      .eq('type', 'entre')
      .order('created_at', { ascending: false })
    if (error) throw error
    return data as Homework[]
  }

  static async createSchoolHomework(params: {
    schoolId: string
    title: string
    description: string | null
    scoring_prompt: string
    initial_message: string | null
    deadline_at: string | null
    start_at: string | null
    target_grades: string[]
    target_classes: string[]
    related_video_id: string | null
  }): Promise<Homework> {
    const { data, error } = await supabase
      .from('homeworks')
      .insert({
        type: 'school',
        school_id: params.schoolId,
        title: params.title,
        description: params.description,
        scoring_prompt: params.scoring_prompt,
        initial_message: params.initial_message,
        deadline_at: params.deadline_at,
        start_at: params.start_at,
        target_grades: params.target_grades,
        target_classes: params.target_classes,
        related_video_id: params.related_video_id,
        is_active: true,
        display_order: 0
      })
      .select()
      .single()
    if (error) throw error
    return data as Homework
  }

  static async updateSchoolHomework(
    id: string,
    updates: Partial<{
      title: string
      description: string | null
      scoring_prompt: string
      initial_message: string | null
      deadline_at: string | null
      start_at: string | null
      target_grades: string[]
      target_classes: string[]
      related_video_id: string | null
      is_active: boolean
    }>
  ): Promise<void> {
    const { error } = await supabase
      .from('homeworks')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
  }

  static async deleteSchoolHomework(id: string): Promise<void> {
    const { error } = await supabase
      .from('homeworks')
      .delete()
      .eq('id', id)
    if (error) throw error
  }

  /**
   * student.school_id つき。アントレ部宿題は全学校の提出が1つの宿題にぶら下がるので、
   * schoolId を渡してサーバー側で絞る（他校の生徒情報をそもそも取得しない）。
   * 全校横断で見るとき（管理者用の学校を選んでいるとき）だけ schoolId を省く。
   */
  static async getSchoolHomeworkSubmissions(
    homeworkId: string,
    opts?: { schoolId?: string }
  ): Promise<SchoolHomeworkSubmissionWithStudent[]> {
    let query = supabase
      .from('homework_submissions')
      .select(opts?.schoolId ? SCHOOL_SUBMISSION_SELECT_SCOPED : SCHOOL_SUBMISSION_SELECT)
      .eq('homework_id', homeworkId)
      .neq('status', 'draft') // drafts (途中保存) aren't visible to teachers

    if (opts?.schoolId) query = query.eq('student.school_id', opts.schoolId)

    const { data, error } = await query.order('submitted_at', { ascending: false })
    if (error) throw error
    return data as unknown as SchoolHomeworkSubmissionWithStudent[]
  }

  // publish=false saves the review without releasing it to the student;
  // publication then happens in bulk via confirmAllSubmissions
  static async reviewSubmission(params: {
    submissionId: string
    reviewerId: string
    grade: 'A' | 'B' | 'C'
    feedbackText: string
    publish?: boolean
  }): Promise<void> {
    const { error } = await supabase
      .from('homework_submissions')
      .update({
        grade: params.grade,
        feedback_text: params.feedbackText,
        teacher_reviewed: params.publish ?? true,
        reviewed_by: params.reviewerId
      })
      .eq('id', params.submissionId)
    if (error) throw error
  }

  /**
   * submissionIds を渡すと、その提出だけを公開する。アントレ部宿題は全校共通なので、
   * 画面に出ている（＝選択中の学校の）提出に限定しないと他校の評価まで公開されてしまう。
   * UPDATE は埋め込みリソースで絞れないため、ID を明示的に渡す。
   */
  static async confirmAllSubmissions(homeworkId: string, submissionIds?: string[]): Promise<void> {
    if (submissionIds && submissionIds.length === 0) return

    let query = supabase
      .from('homework_submissions')
      .update({ teacher_reviewed: true })
      .eq('homework_id', homeworkId)
      .neq('status', 'draft') // never mark an unsubmitted draft as reviewed

    if (submissionIds) {
      query = query.in('id', submissionIds)
    }

    const { error } = await query
    if (error) throw error
  }

  static async generateOverallSummary(homeworkId: string): Promise<OverallSummary> {
    const { data: { session } } = await supabase.auth.getSession()
    const resp = await fetch(`${supabaseUrl}/functions/v1/summarize-homework`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token || ''}` },
      body: JSON.stringify({ homeworkId })
    })
    const data = await resp.json()
    if (!data.success) {
      throw new Error(data.error || '総評の生成に失敗しました')
    }
    return data.overallSummary as OverallSummary
  }

  // school を持つ行があるときだけ学校列を付ける（アントレ部宿題は複数校にまたがる）
  static exportSubmissionsCSV(
    rows: { school?: string; grade: string; class: string; number: string; name: string; status: string; evaluation: string; feedback: string }[],
    homeworkTitle: string
  ): void {
    const withSchool = rows.some(r => r.school !== undefined)
    const headers = [...(withSchool ? ['学校'] : []), '学年', 'クラス', '番号', '名前', '提出状況', '評価', 'フィードバック']
    const csv = [
      headers,
      ...rows.map(r => [...(withSchool ? [r.school ?? '-'] : []), r.grade, r.class, r.number, r.name, r.status, r.evaluation, r.feedback]),
    ]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    const dateStr = new Date().toISOString().split('T')[0]
    link.download = `homework-${homeworkTitle}-${dateStr}.csv`
    link.click()
  }

  // schoolId を渡すと、その学校の生徒の提出だけを数える（アントレ部宿題は全校共通のため必須）。
  // 生徒IDの .in() では絞らない — 大規模校で GET の URL 長が破綻するので埋め込みで絞る。
  // submittedStudentIds は宿題一覧を学年・クラス・グループで絞ったときの数え直し用。
  // 提出は宿題数×生徒数になりうるので、PostgREST の上限（1000行）を超えてもページングで全件読む。
  static async getSubmissionStats(
    homeworkIds: string[],
    opts?: { schoolId?: string }
  ): Promise<Map<string, SubmissionStats>> {
    const map = new Map<string, SubmissionStats>()
    if (homeworkIds.length === 0) return map

    const PAGE = 1000
    for (let from = 0; ; from += PAGE) {
      let query = supabase
        .from('homework_submissions')
        .select(
          opts?.schoolId
            ? 'id, homework_id, student_id, teacher_reviewed, student:users!student_id!inner(school_id)'
            : 'id, homework_id, student_id, teacher_reviewed'
        )
        .in('homework_id', homeworkIds)
        .neq('status', 'draft') // drafts (途中保存) aren't counted as submissions

      if (opts?.schoolId) {
        query = query.eq('student.school_id', opts.schoolId)
      }

      const { data, error } = await query.order('id').range(from, from + PAGE - 1)
      if (error) throw error
      const rows = data as unknown as { homework_id: string; student_id: string; teacher_reviewed: boolean }[]
      for (const row of rows) {
        const cur = map.get(row.homework_id) ?? { submitted: 0, unreviewed: 0, submittedStudentIds: new Set<string>() }
        cur.submitted += 1
        if (!row.teacher_reviewed) cur.unreviewed += 1
        cur.submittedStudentIds.add(row.student_id)
        map.set(row.homework_id, cur)
      }
      if (rows.length < PAGE) break
    }
    return map
  }

  static async getSchoolStudents(schoolId: string): Promise<SchoolStudent[]> {
    const { data, error } = await supabase
      .from('users')
      .select('id, name, email, grade, class, attendance_number')
      .eq('school_id', schoolId)
      .eq('role', 'student')
    if (error) throw error
    return data as SchoolStudent[]
  }

  /**
   * アントレ部宿題の対象生徒＝ is_entre_club グループのメンバー
   * （生徒側の表示条件と同じ: HomeworkPage / VideosPage の inEntreClub 判定）。
   * schoolIds を渡すとその学校に限定する（宿題の対象学校。省略＝全学校）。
   * group_members には student_id と added_by の2つのFKがあるため、埋め込みではなく
   * 3クエリに分けて getSchoolStudents と同じ形で返す。
   */
  static async getEntreClubStudents(schoolIds?: string[] | null): Promise<EntreClubStudent[]> {
    let groupQuery = supabase
      .from('groups')
      .select('id')
      .eq('is_entre_club', true)
    if (schoolIds && schoolIds.length > 0) {
      groupQuery = groupQuery.in('school_id', schoolIds)
    }
    const { data: groups, error: groupsError } = await groupQuery
    if (groupsError) throw groupsError

    const groupIds = (groups ?? []).map((g: { id: string }) => g.id)
    if (groupIds.length === 0) return []

    const { data: members, error: membersError } = await supabase
      .from('group_members')
      .select('student_id')
      .in('group_id', groupIds)
    if (membersError) throw membersError

    const studentIds = Array.from(new Set((members ?? []).map((m: { student_id: string }) => m.student_id)))
    if (studentIds.length === 0) return []

    let userQuery = supabase
      .from('users')
      .select('id, name, email, grade, class, attendance_number, school_id, school:schools(name)')
      .in('id', studentIds)
      .eq('role', 'student')
    if (schoolIds && schoolIds.length > 0) {
      userQuery = userQuery.in('school_id', schoolIds)
    }
    const { data, error } = await userQuery
    if (error) throw error

    type Row = SchoolStudent & { school_id: string | null; school: { name: string } | null }
    return (data as unknown as Row[]).map(({ school, ...student }) => ({
      ...student,
      school_name: school?.name ?? null,
    }))
  }

  // --- Admin methods ---

  static async getAllHomework(): Promise<Homework[]> {
    const { data, error } = await supabase
      .from('homeworks')
      .select('*')
      .order('display_order', { ascending: true })
    if (error) throw error
    return data as Homework[]
  }

  static async createHomework(hw: Omit<Homework, 'id' | 'created_at' | 'updated_at' | 'overall_summary'>): Promise<Homework> {
    const { data, error } = await supabase
      .from('homeworks')
      .insert(hw)
      .select()
      .single()
    if (error) throw error
    return data as Homework
  }

  static async updateHomework(id: string, hw: Partial<Omit<Homework, 'id' | 'created_at' | 'updated_at'>>): Promise<void> {
    const { error } = await supabase
      .from('homeworks')
      .update({ ...hw, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
  }

  static async deleteHomework(id: string): Promise<void> {
    const { error } = await supabase
      .from('homeworks')
      .delete()
      .eq('id', id)
    if (error) throw error
  }
}
